import { useState, useEffect } from 'react';
import utils from '../../../../../lib/utils';
import { EquipmentItem } from 'components/elements/QuickViewButton/components/KeyEquipmentButton/KeyEquipmentButton';
import DocumentBrowseCard from '../../../DocumentBrowseCard';
import { getImageUrl } from '../../../../../lib/cloudinary';
import { algoliaHitToDocumentBrowseCard } from '../../../../../lib/utils/transforms';
import honeybadger from 'lib/errors/honeybadger';
import algolia from 'lib/algolia';
import styles from './Reviewable.module.scss';
import { quickViewClickEvent } from 'lib/analytics/mixpanel/quickViewEvent';
import buyNowBookClickEvent from 'lib/analytics/mixpanel/buyNowBookEvent';
import analyticsService from 'services/analytics.service';
import events from 'lib/analytics/mixpanel/events';
import { Text, Link } from '@americastestkitchen/mise';

type ReviewableProps = {
  reviewable: EquipmentItem;
  bookQueries: (type: string) => Record<string, string>;
};

export type AlgoliaResponseObject = {
  description: string;
  free_sites: ['cio', 'cco', 'atk'];
  free_with_registration_sites: ['cio', 'cco', 'atk'];
  is_kids_content: boolean;
  title: string;
  objectID: string;
  search_price?: string | null;
  search_url?: string | null;
  search_document_klass: string;
};

const onRelatedsClick = (related: AlgoliaResponseObject) => {
  const isBook = related.objectID.toLowerCase().includes('book');
  if (!isBook) {
    quickViewClickEvent(
      related.objectID,
      related.title,
      related.search_document_klass,
    );
  } else {
    buyNowBookClickEvent({
      search_price: related.search_price,
      search_document_klass: related.search_document_klass,
      search_url: related.search_url,
    });
  }
};

const Reviewable = ({ reviewable, bookQueries }: ReviewableProps) => {
  const {
    ctaTitle,
    ctaUrl,
    documentId,
    documentType,
    photo,
    subtitle: reviewSetTitle,
    title: winnerTitle,
    url,
    price,
    asin,
    affiliateName,
  } = reviewable;

  const winnerUrl = utils.checkRelativeLink('atk', url);
  const [relateds, setRelateds] = useState<AlgoliaResponseObject[]>([]);

  useEffect(() => {
    async function getRelateds() {
      const relatedsRequestObj = {
        bookQueries,
        subtitle: reviewSetTitle,
        documentType,
        documentId,
      };

      try {
        const res: AlgoliaResponseObject[] =
          await algolia.getReviewableRelateds(relatedsRequestObj, 'equipment');
        setRelateds(res);
      } catch (error) {
        honeybadger.notify(`Unable to getReviewableRelateds: ${error}`);
      }
    }
    getRelateds();
  }, [bookQueries, documentId, documentType, reviewSetTitle]);

  return (
    <div>
      {winnerTitle && (
        <div className={styles.section}>
          <Text
            as="p"
            className={styles.header}
            variant="labelSecondary"
            weight="bold"
          >
            {`Our Pick For ${reviewSetTitle}`}
          </Text>
          <div
            className={styles.winner}
            data-id={documentId}
            data-link={winnerUrl}
            data-title={winnerTitle}
            data-type={documentType}
          >
            {photo && photo.id ? (
              <a
                className="reviewable__image-link"
                href={winnerUrl}
                onClick={() => {
                  quickViewClickEvent(documentId, winnerTitle, documentType);
                }}
              >
                <div className={styles.imageWrapper}>
                  <img
                    alt={winnerTitle}
                    className={styles.winnerImage}
                    crossOrigin="anonymous"
                    src={getImageUrl(photo.id, {
                      effect: `replace_color:f5f5f5:100:ffffff`,
                    })}
                  />
                </div>
              </a>
            ) : null}
            <div className={styles.winnerLinks}>
              <Link
                href={winnerUrl}
                variant="underline"
                // @ts-expect-error mise typing fixed, update mise or fix error here when next updating this file
                weight="bold"
                textVariant="labelPrimary"
                onClick={() => {
                  quickViewClickEvent(documentId, winnerTitle, documentType);
                }}
              >
                {winnerTitle}
              </Link>
              {ctaUrl ? (
                <>
                  <Link
                    href={ctaUrl}
                    variant="text"
                    onClick={() => {
                      analyticsService.track(events.BUY_NOW_CLICK, {
                        location: 'recipe detail quick-view',
                        price,
                        affiliate_url: ctaUrl,
                        retailer: affiliateName,
                        documentType: documentType,
                        asin,
                      });
                    }}
                  >
                    {ctaTitle}
                  </Link>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
      <div className={styles.section}>
        <Text
          as="p"
          className={styles.header}
          variant="labelSecondary"
          weight="bold"
        >
          {`More With ${reviewSetTitle}`}
        </Text>
        {relateds && relateds.length > 0
          ? relateds.slice(0, 4).map((related) => (
              <div
                key={related.objectID}
                onClick={() => onRelatedsClick(related)}
              >
                <DocumentBrowseCard
                  {...algoliaHitToDocumentBrowseCard(related, {
                    currentSiteKey: 'atk',
                    imageBgColor: 'f5f5f5',
                    includeDescription: false,
                  })}
                  lazyImage={false}
                  visualStyle="quick-view-card"
                  includeFavorite={false}
                />
              </div>
            ))
          : null}
      </div>
    </div>
  );
};

export default Reviewable;
